import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { ReportData } from "@/types";

// 转义 HTML 特殊字符，防止用户输入注入（XSS）
function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// 创建报告HTML内容 - 政府公文风格
function createReportHTML(data: ReportData): string {
  const majorHazardsHTML = data.majorHazards
    .map(
      (h, i) =>
        `<div class="hazard-item major"><span class="index">${i + 1}.</span>${escapeHtml(h)}</div>`,
    )
    .join("");

  const generalHazardsHTML = data.generalHazards
    .map(
      (h, i) =>
        `<div class="hazard-item general"><span class="index">${i + 1}.</span>${escapeHtml(h)}</div>`,
    )
    .join("");

  const suggestionsHTML = data.suggestions
    .map(
      (s, i) =>
        `<div class="suggestion-item"><span class="index">${i + 1}.</span>${escapeHtml(s)}</div>`,
    )
    .join("");

  return `
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      body {
        font-family: "SimSun", "宋体", "PingFang SC", "Microsoft YaHei", serif;
        font-size: 12pt;
        line-height: 1.8;
        color: #333;
        background: #fff;
      }
      .container {
        width: 210mm;
        padding: 15mm 20mm;
        background: #fff;
      }

      /* 页眉 */
      .header {
        text-align: center;
        border-bottom: 3px double #b71c1c;
        padding-bottom: 12px;
        margin-bottom: 20px;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .title {
        font-size: 26pt;
        font-weight: bold;
        color: #b71c1c;
        letter-spacing: 6px;
        margin-bottom: 8px;
        font-family: "SimHei", "黑体", "PingFang SC", sans-serif;
      }
      .report-number {
        font-size: 10pt;
        color: #666;
        font-family: "FangSong", "仿宋", serif;
      }

      /* 基本信息区 */
      .info-section {
        margin-bottom: 18px;
        padding: 12px 15px;
        background: #fafafa;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .info-row {
        display: flex;
        margin-bottom: 6px;
        font-size: 11pt;
      }
      .info-row:last-child {
        margin-bottom: 0;
      }
      .info-label {
        color: #555;
        width: 100px;
        flex-shrink: 0;
        font-weight: 500;
      }
      .info-value {
        color: #222;
        flex: 1;
      }

      /* 章节标题 */
      .section {
        margin-bottom: 18px;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .section-title {
        font-size: 14pt;
        font-weight: bold;
        color: #b71c1c;
        margin-bottom: 10px;
        padding-bottom: 5px;
        border-bottom: 1px solid #ddd;
        font-family: "SimHei", "黑体", sans-serif;
        page-break-after: avoid;
        break-after: avoid;
      }
      .section-content {
        text-align: justify;
        color: #333;
        text-indent: 2em;
        font-size: 11pt;
        orphans: 2;
        widows: 2;
      }

      /* 隐患分组 */
      .hazard-group {
        margin-bottom: 12px;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .hazard-group-title {
        font-size: 12pt;
        font-weight: bold;
        margin-bottom: 8px;
        font-family: "SimHei", "黑体", sans-serif;
      }
      .hazard-group-title.major {
        color: #b71c1c;
      }
      .hazard-group-title.general {
        color: #e65100;
      }
      .hazard-item {
        margin-bottom: 6px;
        padding: 6px 10px;
        color: #333;
        background: #fff;
        border-radius: 3px;
        font-size: 11pt;
        line-height: 1.7;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .hazard-item .index {
        font-weight: bold;
        margin-right: 6px;
      }
      .hazard-item.major {
        border-left: 3px solid #b71c1c;
        background: #fff8f8;
      }
      .hazard-item.general {
        border-left: 3px solid #e65100;
        background: #fff8f0;
      }

      /* 整改建议 */
      .suggestion-item {
        margin-bottom: 6px;
        padding: 6px 10px;
        border-left: 3px solid #1565c0;
        background: #f5f9ff;
        color: #333;
        font-size: 11pt;
        line-height: 1.7;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .suggestion-item .index {
        font-weight: bold;
        margin-right: 6px;
      }

      /* 检查项目表格 */
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 10pt;
        margin-top: 5px;
      }
      thead {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      th, td {
        border: 1px solid #ccc;
        padding: 8px 10px;
        text-align: left;
      }
      tr {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      th {
        background: #f5f5f5;
        font-weight: bold;
        color: #333;
        font-family: "SimHei", "黑体", sans-serif;
      }
      .center {
        text-align: center;
      }
      .text-red {
        color: #b71c1c;
        font-weight: bold;
      }
      .text-orange {
        color: #e65100;
        font-weight: bold;
      }
      .text-green {
        color: #2e7d32;
        font-weight: bold;
      }
      .major-row {
        background: #fff5f5;
      }
      .general-row {
        background: #fff8e1;
      }

      .basis-col {
        font-size: 9pt;
        color: #666;
        line-height: 1.4;
      }

      /* 处理建议 */
      .handling-item {
        margin-bottom: 10px;
        padding: 10px 12px;
        border: 1px solid #e0e0e0;
        border-left: 3px solid #1565c0;
        background: #f5f9ff;
        border-radius: 3px;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .handling-header {
        margin-bottom: 6px;
      }
      .handling-type {
        font-weight: bold;
        color: #1565c0;
        font-size: 11pt;
      }
      .handling-body {
        color: #333;
        font-size: 10pt;
      }
      .handling-measure {
        margin-bottom: 6px;
        line-height: 1.6;
      }
      .handling-meta {
        display: flex;
        gap: 16px;
        font-size: 9pt;
        color: #666;
      }

      /* 签名区 */
      .signature-section {
        margin-top: 25px;
        padding-top: 15px;
        border-top: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .signature-box {
        width: 46%;
      }
      .signature-label {
        color: #555;
        margin-bottom: 8px;
        font-size: 11pt;
        font-weight: 500;
      }
      .signature-img {
        width: 100%;
        height: 70px;
        border: 1px solid #ddd;
        background: #fafafa;
        border-radius: 3px;
      }

      /* 页脚 */
      .footer {
        margin-top: 20px;
        padding-top: 10px;
        border-top: 1px solid #eee;
        text-align: center;
        font-size: 9pt;
        color: #999;
      }
    </style>
    <div class="container">
      <div class="header">
        <div class="title">燃气安全检查报告</div>
        <div class="report-number">报告编号：${escapeHtml(data.reportNumber)}</div>
      </div>

      <div class="info-section">
        <div class="info-row">
          <span class="info-label">检查类型：</span>
          <span class="info-value">${escapeHtml(data.checkType)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">被检查单位：</span>
          <span class="info-value">${escapeHtml(data.inspectedUnit)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">检查地址：</span>
          <span class="info-value">${escapeHtml(data.inspectedAddress)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">检查人员：</span>
          <span class="info-value">${escapeHtml(data.inspector)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">检查日期：</span>
          <span class="info-value">${escapeHtml(data.checkDate)}</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">一、检查概况</div>
        <div class="section-content">${escapeHtml(data.overview)}</div>
      </div>

      <div class="section">
        <div class="section-title">二、发现的主要问题</div>
        ${
          data.majorHazards.length > 0
            ? `
          <div class="hazard-group">
            <div class="hazard-group-title major">（一）重大隐患（${data.majorHazards.length}项）</div>
            ${majorHazardsHTML}
          </div>
        `
            : ""
        }
        ${
          data.generalHazards.length > 0
            ? `
          <div class="hazard-group">
            <div class="hazard-group-title general">（二）一般隐患（${data.generalHazards.length}项）</div>
            ${generalHazardsHTML}
          </div>
        `
            : ""
        }
        ${
          data.majorHazards.length === 0 && data.generalHazards.length === 0
            ? '<div class="section-content">本次检查未发现安全隐患。</div>'
            : ""
        }
      </div>

      <div class="section">
        <div class="section-title">三、整改建议</div>
        <div class="suggestions">${suggestionsHTML}</div>
      </div>

      ${
        data.inspectorSignature || data.unitRepresentativeSignature
          ? `
        <div class="signature-section">
          ${
            data.inspectorSignature
              ? `
            <div class="signature-box">
              <div class="signature-label">检查人员签名：</div>
              <img class="signature-img" src="${escapeHtml(data.inspectorSignature ?? "")}" alt="检查人员签名" />
            </div>
          `
              : '<div class="signature-box"></div>'
          }
          ${
            data.unitRepresentativeSignature
              ? `
            <div class="signature-box">
              <div class="signature-label">被检查单位签名：</div>
              <img class="signature-img" src="${escapeHtml(data.unitRepresentativeSignature ?? "")}" alt="被检查单位签名" />
            </div>
          `
              : ""
          }
        </div>
      `
          : ""
      }

      <div class="footer">
      </div>
    </div>
  `;
}

// 内容块的起止边界
interface ContentBlock {
  start: number;
  end: number;
}

// 收集不可拆分的原子内容块边界。
// 注意：不包含 .section / .hazard-group —— 整节往往高于一页 A4，
// 若视为不可拆分会导致页底大面积空白。仅保护小颗粒块不被跨页截断。
function collectContentBlocks(container: HTMLElement): ContentBlock[] {
  const containerRect = container.getBoundingClientRect();
  const blocks: ContentBlock[] = [];

  const selectors = [
    ".hazard-item",
    ".suggestion-item",
    ".header",
    ".info-section",
    ".signature-section",
    "tr",
    "thead",
  ];

  selectors.forEach((selector) => {
    container.querySelectorAll(selector).forEach((el) => {
      const rect = el.getBoundingClientRect();
      const start = rect.top - containerRect.top;
      const end = rect.bottom - containerRect.top;
      if (end > 0) {
        blocks.push({ start, end });
      }
    });
  });

  // 章节标题与其后内容绑定（keep-with-next），避免标题孤立在页底。
  // 块高 = 标题高 + 约两行内容缓冲。
  container.querySelectorAll(".section-title").forEach((el) => {
    const rect = el.getBoundingClientRect();
    const start = rect.top - containerRect.top;
    const titleHeight = rect.height;
    const end = start + titleHeight * 3;
    if (end > 0) {
      blocks.push({ start, end });
    }
  });

  return blocks.sort((a, b) => a.start - b.start);
}

// 在内容块边界中选择最佳页面结束位置，避免截断关键内容
function findBestPageEnd(
  currentY: number,
  naturalEnd: number,
  blocks: ContentBlock[],
  totalHeight: number,
): number {
  const pageHeight = naturalEnd - currentY;
  // 页面必须保留的最小内容量（20%）
  const minContentHeight = pageHeight * 0.2;

  // 找到所有会跨页截断的内容块
  for (const block of blocks) {
    // 跳过已经完全在当前页之前的块
    if (block.end <= currentY) continue;
    // 跳过完全在下一页及之后的块
    if (block.start >= naturalEnd) continue;

    // 块与当前捕获范围有交集，检查是否会被截断
    if (block.start >= currentY && block.end > naturalEnd) {
      // 该块从当前页范围内开始但延伸到 naturalEnd 之后 → 会被跨页截断
      const proposedEnd = block.start;
      if (proposedEnd - currentY >= minContentHeight) {
        // 该块之前有足够内容，页断在其起始位置
        return proposedEnd;
      }
      // 该块从页面太靠前的位置开始（内容太少无法提前断页），
      // 继续检查其内部的子块（如 hazard-group、tr）是否有更好的断点
      continue;
    }
  }

  let bestEnd = naturalEnd;

  // 剩余内容不足 12% 页面高度时，直接合入当前页避免孤页
  const remaining = totalHeight - bestEnd;
  if (remaining > 0 && remaining < pageHeight * 0.12) {
    bestEnd = totalHeight;
  }

  return Math.min(bestEnd, totalHeight);
}

// 生成 PDF 文件
export async function generatePDF(data: ReportData): Promise<void> {
  const container = document.createElement("div");
  container.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    width: 210mm;
    background: #fff;
  `;
  container.innerHTML = createReportHTML(data);
  document.body.appendChild(container);

  try {
    await new Promise((resolve) => setTimeout(resolve, 300));

    const pageWidth = container.scrollWidth;
    const totalHeight = container.scrollHeight;
    // A4 比例: 297/210
    const pageHeight = pageWidth * (297 / 210);

    // 收集所有不可拆分的内容块
    const contentBlocks = collectContentBlocks(container);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    let currentY = 0;
    let pageNum = 1;

    while (currentY < totalHeight) {
      if (pageNum > 1) {
        doc.addPage();
      }

      const naturalEnd = currentY + pageHeight;
      const pageEnd = findBestPageEnd(
        currentY,
        naturalEnd,
        contentBlocks,
        totalHeight,
      );
      const captureHeight = Math.min(
        pageEnd - currentY,
        totalHeight - currentY,
      );

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: pageWidth,
        windowHeight: captureHeight,
        y: currentY,
        height: captureHeight,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const imgHeightMm = (canvas.height * 210) / canvas.width;
      doc.addImage(imgData, "JPEG", 0, 0, 210, imgHeightMm);

      currentY = pageEnd;
      pageNum++;
    }

    doc.save(`燃气安全检查报告_${data.reportNumber}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
